import { Link } from 'react-router-dom';
import Nav from './Nav';

// The conceptual model in one page — the map before the territory. Everything
// deeper is taught in place by explain mode (the toggle in the nav), which
// annotates live data with how each number is computed.

const sections = [
  {
    heading: 'Objects',
    body: 'Everything you save is an object: a claim, a note, a source, a person, a tag. Objects are deliberately simple and atomic — one unit of content each.',
  },
  {
    heading: 'Relations',
    body: 'Meaning lives in the connections. A relation is a named, directional edge between two objects — "supports", "contradicts", "references", "trusts" — with a polarity: +1 asserts it, −1 denies it. You can invent new relation types freely; the graph never enforces structure when you write, only when it reads.',
  },
  {
    heading: 'Voting',
    body: 'You can vote agree or disagree on any connection, one vote per person. The relation type is the dimension: voting on "supports" is an epistemic judgment, voting on "trusts" a social one.',
  },
  {
    heading: 'The activation boundary',
    body: 'A connection nobody has voted on is inert — it counts for nothing in any trust computation, no matter who or what created it. Since only humans vote, every active edge was verified by a person. This is the quarantine that lets AI gather data at scale without AI deciding what is credible.',
  },
  {
    heading: 'Lenses',
    body: 'There is no single trust algorithm here. A lens is one perspective for scoring objects — by community votes, by coherence of surrounding evidence, by the author\'s standing in the trust network. Lenses are shown side by side, and their disagreement is information. The combined score is currently a plain average, and that is a placeholder on purpose.',
  },
  {
    heading: 'Provenance',
    body: 'Every connection wears a badge: verified (a human voted on it) or AI-suggested (nobody has yet). There is no hidden curation — the boundary between human-checked and machine-gathered is always visible.',
  },
  {
    heading: 'Privacy',
    body: 'Objects have two independent privacy switches: who can see them, and whether an external AI model may ever read them. Sealed objects are never sent to any model — enforced in one code chokepoint, not by policy.',
  },
  {
    heading: 'Domains',
    body: 'Domains in the Trust atlas are just tags. Any tag with members acts as a domain, subcategories roll up into parents, and no taxonomy is imposed — categories are whatever the graph grows.',
  },
];

function About() {
  return (
    <div>
      <Nav />
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 20px 40px' }}>
        <h1 style={{ marginTop: 0 }}>How Worldview works</h1>
        <p style={{ color: 'var(--wv-muted)', lineHeight: 1.6 }}>
          Worldview is a claim graph with trust arithmetic. Most platforms rank content by what
          holds your attention; here, content is ranked by perspectives on its credibility — and
          every score can show its work.
        </p>

        {sections.map((s) => (
          <div key={s.heading} style={{ marginBottom: '18px' }}>
            <h2 style={{ fontSize: '17px', marginBottom: '4px' }}>{s.heading}</h2>
            <p style={{ margin: 0, color: 'var(--wv-text)', lineHeight: 1.6 }}>{s.body}</p>
          </div>
        ))}

        <div
          style={{
            marginTop: '28px',
            padding: '14px 16px',
            border: '1px solid var(--wv-border)6fe',
            borderRadius: '10px',
            background: 'color-mix(in srgb, var(--wv-meta) 12%, transparent)',
            color: 'var(--wv-meta)',
            lineHeight: 1.6,
            fontSize: '14px',
          }}
        >
          The rest is taught in place: switch on <strong>Explain mode</strong> in the nav and small{' '}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '14px',
              height: '14px',
              border: '1px solid color-mix(in srgb, var(--wv-meta) 50%, transparent)',
              borderRadius: '50%',
              background: 'var(--wv-surface)',
              fontSize: '10px',
              fontStyle: 'italic',
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
            }}
          >
            i
          </span>{' '}
          markers appear beside scores, badges, and rankings, explaining each one against the live
          data you are looking at. A good place to start is the <Link to="/trust">Trust atlas</Link>.
        </div>
      </div>
    </div>
  );
}

export default About;
